(() => {
  const ROOT_SELECTOR = "[data-aqj-root]";
  const IOS_VIDEO_SOURCE =
    "https://cdn.shopify.com/videos/c/o/v/3fd094baf769456bb3af654f35a81ba3.mp4";
  const clamp = (value, min = 0, max = 1) =>
    Math.min(max, Math.max(min, value));

  function normalizeCachedMarkup(root) {
    const heroPrompt = root.querySelector(".aqj-hero__prompt");
    if (heroPrompt) {
      const promptText = heroPrompt.querySelector("span");
      const promptArrow = heroPrompt.querySelector("i");
      if (promptText) promptText.textContent = "DESLIZE PARA CIMA";
      if (promptArrow) promptArrow.textContent = "↑";
    }

    const cachedPanel = root.querySelector(
      ".aqj-purchase__panel",
    );

    if (cachedPanel) {
      const purchaseIntro = document.createElement("div");
      purchaseIntro.className = "aqj-purchase__intro";

      [...cachedPanel.children]
        .slice(0, 3)
        .forEach((node) => purchaseIntro.appendChild(node));

      cachedPanel.parentNode?.insertBefore(
        purchaseIntro,
        cachedPanel,
      );
      cachedPanel.classList.remove("aqj-purchase__panel");
      cachedPanel.classList.add("aqj-purchase__conversion");
    }

    const purchaseConversion = root.querySelector(
      ".aqj-purchase__conversion",
    );
    const promotion = purchaseConversion?.querySelector(
      ":scope > .aqj-promo",
    );
    const price = purchaseConversion?.querySelector(
      ":scope > .aqj-price",
    );

    if (
      promotion &&
      price &&
      price.compareDocumentPosition(promotion) &
        Node.DOCUMENT_POSITION_FOLLOWING
    ) {
      purchaseConversion.insertBefore(promotion, price);
    }

    const promotionCopy = promotion?.querySelector("p");
    if (promotionCopy) {
      [...promotionCopy.childNodes].forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE) return;
        node.textContent = node.textContent.replace(
          /Promoção\s+válida\s+apenas\s+hoje,\s+dia/iu,
          "Válida apenas hoje, dia",
        );
      });
    }

    const sizeGuideTrigger = root.querySelector(
      "[data-aqj-size-open]",
    );
    if (sizeGuideTrigger) {
      sizeGuideTrigger.textContent = "Ver guia de tamanhos";
    }

    const cachedVideo = root.querySelector("[data-aqj-video]");
    if (!cachedVideo) return;

    [...cachedVideo.querySelectorAll("source")].forEach(
      (source) => {
        const sourceUrl = source.getAttribute("src");
        if (sourceUrl?.includes("#t=0.001")) {
          source.setAttribute(
            "src",
            sourceUrl.replace("#t=0.001", ""),
          );
        }
      },
    );

    if (
      !cachedVideo.querySelector(
        'source[media="(max-width: 800px)"]',
      )
    ) {
      const mobileSource = document.createElement("source");
      mobileSource.media = "(max-width: 800px)";
      mobileSource.src =
        root.dataset.aqjIosVideo || IOS_VIDEO_SOURCE;
      mobileSource.type = "video/mp4";
      cachedVideo.insertBefore(
        mobileSource,
        cachedVideo.firstChild,
      );
    }
  }

  function initRoot(root) {
    if (!root || root.dataset.aqjReady === "true") return;
    normalizeCachedMarkup(root);
    root.dataset.aqjReady = "true";
    document.body.classList.add("aqj-landing-active");

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const isIOSWebKit =
      /iP(hone|ad|od)/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" &&
        navigator.maxTouchPoints > 1);
    const cleanup = [];
    const hero = root.querySelector("[data-aqj-hero]");
    const story = root.querySelector("[data-aqj-story]");
    const video = root.querySelector("[data-aqj-video]");
    const videoShell = root.querySelector(
      "[data-aqj-video-shell]",
    );
    const stickyCta = root.querySelector(
      "[data-aqj-sticky-cta]",
    );
    const purchase = root.querySelector("[data-aqj-purchase]");
    const finalPurchaseButton = root.querySelector(
      "[data-aqj-submit]",
    );
    const purchaseLinks = [
      ...root.querySelectorAll("[data-aqj-purchase-link]"),
    ];
    let isDisposed = false;
    let animationFrame = 0;
    let desiredVideoTime = 0;
    let lastScene = -1;
    let videoWarmInFlight = false;
    let videoUnlocked = false;
    let paintIOSVideoFrame = null;
    let selectCarouselMedia = null;

    root.classList.toggle("aqj--ios-webkit", isIOSWebKit);

    const seekVideo = () => {
      if (
        !video ||
        video.readyState < HTMLMediaElement.HAVE_METADATA ||
        !Number.isFinite(video.duration) ||
        video.duration <= 0 ||
        video.seeking ||
        videoWarmInFlight ||
        (isIOSWebKit && !videoUnlocked)
      ) {
        return;
      }

      const target = clamp(
        desiredVideoTime,
        0,
        Math.max(0, video.duration - 0.04),
      );
      const tolerance = isIOSWebKit ? 0.05 : 1 / 120;

      if (Math.abs(video.currentTime - target) <= tolerance) {
        return;
      }

      if (isIOSWebKit && paintIOSVideoFrame) {
        paintIOSVideoFrame(target);
        return;
      }

      try {
        video.currentTime = target;
      } catch (_) {
        requestRender();
      }
    };

    const renderScroll = () => {
      animationFrame = 0;

      if (hero && !reducedMotion) {
        const rect = hero.getBoundingClientRect();
        const distance = Math.max(
          1,
          hero.offsetHeight - window.innerHeight,
        );
        const rawProgress = clamp(-rect.top / distance);
        const progress = clamp(rawProgress / 0.88);
        const copyProgress = clamp((progress - 0.68) / 0.24);
        const promptProgress = 1 - clamp(progress / 0.2);

        hero.style.setProperty(
          "--hero-mask-full",
          `${(progress * 100).toFixed(2)}%`,
        );
        hero.style.setProperty(
          "--hero-mask-fade",
          `${(progress * 100 + 8).toFixed(2)}%`,
        );
        hero.style.setProperty(
          "--hero-after-opacity",
          clamp(progress / 0.08).toFixed(4),
        );
        hero.style.setProperty(
          "--hero-copy-opacity",
          copyProgress.toFixed(4),
        );
        hero.style.setProperty(
          "--hero-copy-y",
          `${((1 - copyProgress) * 24).toFixed(2)}px`,
        );
        hero.style.setProperty(
          "--hero-prompt-opacity",
          promptProgress.toFixed(4),
        );
        hero.style.setProperty(
          "--hero-prompt-y",
          `${((1 - promptProgress) * -12).toFixed(2)}px`,
        );
      }

      if (story) {
        const rect = story.getBoundingClientRect();
        const distance = Math.max(
          1,
          story.offsetHeight - window.innerHeight,
        );
        const progress = clamp(-rect.top / distance);
        const scene = Math.round(progress * 4);

        if (scene !== lastScene) {
          story.dataset.scene = String(scene);
          lastScene = scene;
        }

        if (
          video &&
          Number.isFinite(video.duration) &&
          video.duration > 0
        ) {
          desiredVideoTime =
            progress * Math.max(0, video.duration - 0.04);
          seekVideo();
        }
      }

      if (stickyCta && story && purchase) {
        const viewportHeight = window.innerHeight;
        const storyRect = story.getBoundingClientRect();
        const finalTargetRect = (
          finalPurchaseButton || purchase
        ).getBoundingClientRect();
        const stickyBottom =
          Number.parseFloat(
            window.getComputedStyle(stickyCta).bottom,
          ) || 16;
        const stickyTop =
          viewportHeight -
          stickyCta.offsetHeight -
          stickyBottom;
        const reachedFinalButton =
          finalTargetRect.top <= stickyTop + 1;
        const shouldShow =
          storyRect.bottom <= viewportHeight &&
          !reachedFinalButton;

        stickyCta.classList.toggle("is-visible", shouldShow);
        stickyCta.classList.toggle(
          "is-past-purchase",
          reachedFinalButton,
        );
        stickyCta.setAttribute(
          "aria-hidden",
          String(!shouldShow),
        );
      }
    };

    const requestRender = () => {
      if (!animationFrame) {
        animationFrame = window.requestAnimationFrame(renderScroll);
      }
    };

    const scrollToPurchase = (event) => {
      if (!purchase) return;
      event.preventDefault();
      const target = finalPurchaseButton || purchase;
      const targetRect = target.getBoundingClientRect();
      const stickyRect = stickyCta?.getBoundingClientRect();
      const desiredViewportTop =
        stickyRect && stickyRect.height > 0
          ? stickyRect.top
          : window.innerHeight * 0.84;
      const targetTop = Math.max(
        0,
        window.scrollY + targetRect.top - desiredViewportTop,
      );

      window.scrollTo({
        top: targetTop,
        behavior: reducedMotion ? "auto" : "smooth",
      });
    };

    purchaseLinks.forEach((link) =>
      link.addEventListener("click", scrollToPurchase),
    );
    cleanup.push(() => {
      purchaseLinks.forEach((link) =>
        link.removeEventListener("click", scrollToPurchase),
      );
    });

    window.addEventListener("scroll", requestRender, {
      passive: true,
    });
    window.addEventListener("resize", requestRender);
    cleanup.push(() => {
      window.removeEventListener("scroll", requestRender);
      window.removeEventListener("resize", requestRender);
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
    });

    if (video) {
      let videoLoadRequested = false;
      let warmFrameCallback = 0;
      let warmFrameTimeout = 0;
      let warmTimeUpdateHandler = null;
      let iosPaintInFlight = false;
      let iosPaintQueued = false;
      let iosPaintTarget = 0;
      let iosPaintFrameCallback = 0;
      let iosPaintNudgeTimeout = 0;
      let iosPaintHardTimeout = 0;
      let iosPaintTimeUpdateHandler = null;

      const markVideoReady = (force = false) => {
        if (
          videoShell &&
          (force ||
            (!isIOSWebKit &&
              video.readyState >=
                HTMLMediaElement.HAVE_CURRENT_DATA))
        ) {
          videoShell.classList.add("is-video-ready");
        }
      };

      const cancelVideoFrameCallback = (callbackId) => {
        if (
          callbackId &&
          typeof video.cancelVideoFrameCallback === "function"
        ) {
          try {
            video.cancelVideoFrameCallback(callbackId);
          } catch (_) {}
        }
      };

      const clearWarmFrameWait = () => {
        cancelVideoFrameCallback(warmFrameCallback);
        warmFrameCallback = 0;
        if (warmFrameTimeout) {
          window.clearTimeout(warmFrameTimeout);
          warmFrameTimeout = 0;
        }
        if (warmTimeUpdateHandler) {
          video.removeEventListener(
            "timeupdate",
            warmTimeUpdateHandler,
          );
          warmTimeUpdateHandler = null;
        }
      };

      const clearIOSPaintWait = () => {
        cancelVideoFrameCallback(iosPaintFrameCallback);
        iosPaintFrameCallback = 0;
        if (iosPaintNudgeTimeout) {
          window.clearTimeout(iosPaintNudgeTimeout);
          iosPaintNudgeTimeout = 0;
        }
        if (iosPaintHardTimeout) {
          window.clearTimeout(iosPaintHardTimeout);
          iosPaintHardTimeout = 0;
        }
        if (iosPaintTimeUpdateHandler) {
          video.removeEventListener(
            "timeupdate",
            iosPaintTimeUpdateHandler,
          );
          iosPaintTimeUpdateHandler = null;
        }
      };

      const detachWarmListeners = () => {
        root.removeEventListener("touchstart", warmVideo);
        root.removeEventListener("pointerdown", warmVideo);
      };

      const loadVideoOnce = () => {
        if (videoLoadRequested) return;
        videoLoadRequested = true;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute("muted", "");
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");
        try {
          video.load();
        } catch (_) {}
      };

      const finishWarmVideo = (framePresented) => {
        if (isDisposed) return;
        clearWarmFrameWait();
        video.pause();
        videoWarmInFlight = false;

        if (!framePresented) {
          requestRender();
          return;
        }

        videoUnlocked = true;
        detachWarmListeners();
        markVideoReady(true);
        requestRender();
      };

      const waitForWarmVideoFrame = () => {
        const handleWarmFrame = () => finishWarmVideo(true);

        if (
          typeof video.requestVideoFrameCallback === "function"
        ) {
          warmFrameCallback =
            video.requestVideoFrameCallback(handleWarmFrame);
        } else {
          warmTimeUpdateHandler = handleWarmFrame;
          video.addEventListener(
            "timeupdate",
            warmTimeUpdateHandler,
            { once: true },
          );
        }

        warmFrameTimeout = window.setTimeout(
          () => finishWarmVideo(false),
          900,
        );
      };

      const warmVideo = () => {
        if (
          videoUnlocked ||
          videoWarmInFlight ||
          isDisposed
        ) {
          return;
        }
        videoWarmInFlight = true;
        loadVideoOnce();

        if (isIOSWebKit) {
          waitForWarmVideoFrame();
        }

        let playResult;
        try {
          playResult = video.play();
        } catch (_) {
          clearWarmFrameWait();
          videoWarmInFlight = false;
          requestRender();
          return;
        }

        if (isIOSWebKit) {
          if (playResult && typeof playResult.catch === "function") {
            playResult.catch(() => {
              clearWarmFrameWait();
              videoWarmInFlight = false;
              if (!isDisposed) requestRender();
            });
          }
          return;
        }

        const finishNonIOSWarmVideo = () => {
          window.requestAnimationFrame(() =>
            finishWarmVideo(true),
          );
        };

        if (playResult && typeof playResult.then === "function") {
          playResult
            .then(finishNonIOSWarmVideo)
            .catch(() => {
              videoWarmInFlight = false;
              if (!isDisposed) requestRender();
            });
        } else {
          finishNonIOSWarmVideo();
        }
      };

      const finishIOSPaint = (framePresented) => {
        if (isDisposed || !iosPaintInFlight) return;
        clearIOSPaintWait();
        video.pause();
        iosPaintInFlight = false;

        if (framePresented) {
          markVideoReady(true);
        }

        const latestTarget = clamp(
          desiredVideoTime,
          0,
          Math.max(0, video.duration - 0.04),
        );
        const targetChanged =
          Math.abs(latestTarget - iosPaintTarget) > 0.05;

        if (iosPaintQueued || targetChanged) {
          iosPaintQueued = false;
          window.requestAnimationFrame(() => {
            if (!isDisposed) paintIOSVideoFrame(latestTarget);
          });
        }
      };

      const beginIOSPaint = (target) => {
        iosPaintInFlight = true;
        iosPaintQueued = false;
        iosPaintTarget = target;

        const handlePresentedFrame = () =>
          finishIOSPaint(true);

        if (
          typeof video.requestVideoFrameCallback === "function"
        ) {
          iosPaintFrameCallback =
            video.requestVideoFrameCallback(
              handlePresentedFrame,
            );
        }

        try {
          video.currentTime = target;
        } catch (_) {
          finishIOSPaint(false);
          return;
        }

        iosPaintNudgeTimeout = window.setTimeout(() => {
          if (!iosPaintInFlight || isDisposed) return;

          if (
            typeof video.requestVideoFrameCallback !== "function"
          ) {
            iosPaintTimeUpdateHandler = handlePresentedFrame;
            video.addEventListener(
              "timeupdate",
              iosPaintTimeUpdateHandler,
              { once: true },
            );
          }

          let playResult;
          try {
            playResult = video.play();
          } catch (_) {
            finishIOSPaint(false);
            return;
          }

          if (
            playResult &&
            typeof playResult.catch === "function"
          ) {
            playResult.catch(() => finishIOSPaint(false));
          }
        }, 90);

        iosPaintHardTimeout = window.setTimeout(
          () => finishIOSPaint(false),
          420,
        );
      };

      paintIOSVideoFrame = (target) => {
        iosPaintTarget = target;
        if (iosPaintInFlight) {
          iosPaintQueued = true;
          return;
        }
        beginIOSPaint(target);
      };

      const handleVideoReady = () => {
        markVideoReady();
        requestRender();
      };

      const videoRenderEvents = [
        "loadedmetadata",
        "durationchange",
      ];
      const videoReadyEvents = [
        "loadeddata",
        "canplay",
        "canplaythrough",
        "seeked",
      ];

      videoRenderEvents.forEach((eventName) =>
        video.addEventListener(eventName, requestRender),
      );
      videoReadyEvents.forEach((eventName) =>
        video.addEventListener(eventName, handleVideoReady),
      );
      root.addEventListener("touchstart", warmVideo, {
        passive: true,
      });
      root.addEventListener("pointerdown", warmVideo, {
        passive: true,
      });
      loadVideoOnce();
      markVideoReady();

      cleanup.push(() => {
        videoRenderEvents.forEach((eventName) =>
          video.removeEventListener(eventName, requestRender),
        );
        videoReadyEvents.forEach((eventName) =>
          video.removeEventListener(eventName, handleVideoReady),
        );
        clearWarmFrameWait();
        clearIOSPaintWait();
        detachWarmListeners();
        video.pause();
        paintIOSVideoFrame = null;
        videoShell?.classList.remove("is-video-ready");
      });
    }
    renderScroll();

    const revealItems = [
      ...root.querySelectorAll("[data-aqj-reveal]"),
    ];

    if (
      reducedMotion ||
      !("IntersectionObserver" in window)
    ) {
      revealItems.forEach((item) =>
        item.classList.add("is-revealed"),
      );
    } else {
      const revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-revealed");
            revealObserver.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: "0px 0px -8% 0px",
        },
      );
      revealItems.forEach((item) =>
        revealObserver.observe(item),
      );
      cleanup.push(() => revealObserver.disconnect());
    }

    const count = root.querySelector("[data-aqj-count]");
    let counterFrame = 0;

    if (count) {
      if (
        reducedMotion ||
        !("IntersectionObserver" in window)
      ) {
        count.textContent = "8 900";
      } else {
        const countObserver = new IntersectionObserver(
          ([entry], observer) => {
            if (!entry || !entry.isIntersecting) return;
            observer.disconnect();
            const startedAt = performance.now();
            const formatter = new Intl.NumberFormat("pt-PT");

            const tick = (now) => {
              const progress = clamp(
                (now - startedAt) / 1800,
              );
              const eased = 1 - Math.pow(1 - progress, 4);
              count.textContent = formatter.format(
                Math.round(eased * 8900),
              );
              if (progress < 1) {
                counterFrame =
                  window.requestAnimationFrame(tick);
              }
            };

            counterFrame = window.requestAnimationFrame(tick);
          },
          { threshold: 0.55 },
        );
        countObserver.observe(count);
        cleanup.push(() => countObserver.disconnect());
      }
    }

    cleanup.push(() => {
      if (counterFrame) {
        window.cancelAnimationFrame(counterFrame);
      }
    });

    const track = root.querySelector(
      "[data-aqj-carousel-track]",
    );

    if (track) {
      const slides = [...track.children];
      const current = root.querySelector("[data-aqj-current]");
      const progressBar = root.querySelector(
        "[data-aqj-progress]",
      );
      const previousButton = root.querySelector(
        "[data-aqj-prev]",
      );
      const nextButton = root.querySelector("[data-aqj-next]");
      let activeIndex = 0;

      const renderCarouselState = () => {
        if (current) {
          current.textContent = String(
            activeIndex + 1,
          ).padStart(2, "0");
        }
        if (progressBar && slides.length) {
          progressBar.style.width = `${
            ((activeIndex + 1) / slides.length) * 100
          }%`;
        }
      };

      const goTo = (requestedIndex) => {
        if (!slides.length) return;
        activeIndex =
          (requestedIndex + slides.length) % slides.length;
        track.scrollTo({
          left: track.clientWidth * activeIndex,
          behavior: "smooth",
        });
        renderCarouselState();
      };

      selectCarouselMedia = (mediaId) => {
        if (!mediaId) return;
        const requestedIndex = slides.findIndex(
          (slide) =>
            String(slide.dataset.aqjMediaId || "") ===
            String(mediaId),
        );
        if (requestedIndex >= 0) {
          goTo(requestedIndex);
        }
      };

      const syncFromScroll = () => {
        if (!track.clientWidth || !slides.length) return;
        activeIndex = clamp(
          Math.round(track.scrollLeft / track.clientWidth),
          0,
          slides.length - 1,
        );
        renderCarouselState();
      };

      const goPrevious = () => goTo(activeIndex - 1);
      const goNext = () => goTo(activeIndex + 1);

      previousButton?.addEventListener("click", goPrevious);
      nextButton?.addEventListener("click", goNext);
      track.addEventListener("scroll", syncFromScroll, {
        passive: true,
      });
      renderCarouselState();

      cleanup.push(() => {
        previousButton?.removeEventListener(
          "click",
          goPrevious,
        );
        nextButton?.removeEventListener("click", goNext);
        track.removeEventListener("scroll", syncFromScroll);
        selectCarouselMedia = null;
      });
    }

    const promotionDate = root.querySelector(
      "[data-aqj-promotion-date]",
    );

    const updatePromotionDate = () => {
      if (!promotionDate) return;
      promotionDate.textContent = new Intl.DateTimeFormat(
        "pt-PT",
        {
          day: "2-digit",
          month: "long",
          year: "numeric",
          timeZone: "Europe/Lisbon",
        },
      ).format(new Date());
    };

    updatePromotionDate();
    const dateInterval = window.setInterval(
      updatePromotionDate,
      60000,
    );
    cleanup.push(() => window.clearInterval(dateInterval));

    const buyForm = root.querySelector("[data-aqj-buy-form]");
    if (buyForm) {
      const optionGroups = [
        ...buyForm.querySelectorAll("[data-aqj-option-group]"),
      ].sort(
        (first, second) =>
          Number(first.dataset.optionPosition || 0) -
          Number(second.dataset.optionPosition || 0),
      );
      const optionInputs = [
        ...buyForm.querySelectorAll("[data-aqj-option]"),
      ];
      const variantInput = buyForm.querySelector(
        "[data-aqj-variant-id]",
      );
      const submitButton = buyForm.querySelector(
        "[data-aqj-submit]",
      );
      const status = buyForm.querySelector(
        "[data-aqj-variant-status]",
      );
      const variantsElement = buyForm.querySelector(
        "[data-aqj-variants]",
      );
      const sizeOpenButton = buyForm.querySelector(
        "[data-aqj-size-open]",
      );
      const sizeDialog = buyForm.querySelector(
        "[data-aqj-size-dialog]",
      );
      const colorDrawer = buyForm.querySelector(
        "[data-aqj-color-drawer]",
      );
      const colorSummarySwatch = buyForm.querySelector(
        "[data-aqj-color-summary-swatch]",
      );
      const sizeCloseButtons = [
        ...buyForm.querySelectorAll("[data-aqj-size-close]"),
      ];
      const availableLabel =
        submitButton?.dataset.availableLabel?.trim() ||
        submitButton?.textContent.trim() ||
        "Garantir já o meu";
      let variants = [];

      try {
        variants = JSON.parse(
          variantsElement?.textContent || "[]",
        );
      } catch (_) {}

      const syncVariant = (shouldSyncCarousel = false) => {
        const selectedOptions = optionGroups.map((group) => {
          const selectedInput = group.querySelector(
            "[data-aqj-option]:checked",
          );
          const selectedValue = selectedInput?.value || "";
          const selectedLabel = group.querySelector(
            "[data-aqj-selected-value]",
          );

          if (selectedLabel) {
            selectedLabel.textContent = selectedValue;
          }
          if (
            group.classList.contains("aqj-option-group--cor") &&
            colorSummarySwatch
          ) {
            const selectedSwatch = selectedInput
              ?.closest(".aqj-option-value")
              ?.querySelector(".aqj-option-value__swatch");
            if (selectedSwatch) {
              colorSummarySwatch.className =
                selectedSwatch.className;
              colorSummarySwatch.setAttribute(
                "data-aqj-color-summary-swatch",
                "",
              );
            }
          }
          return selectedValue;
        });
        const variant = optionGroups.length
          ? variants.find(
              (candidate) =>
                candidate.options.length ===
                  selectedOptions.length &&
                candidate.options.every(
                  (option, index) =>
                    String(option) === selectedOptions[index],
                ),
            )
          : variants.find(
              (candidate) =>
                String(candidate.id) === variantInput?.value,
            ) || variants[0];

        if (!variant) {
          if (variantInput) variantInput.value = "";
          if (submitButton) {
            submitButton.disabled = true;
            submitButton.textContent =
              "Combinação indisponível";
          }
          if (status) {
            status.textContent =
              "Esta combinação não está disponível.";
          }
          return;
        }

        if (variantInput) {
          variantInput.value = String(variant.id);
        }
        if (submitButton) {
          submitButton.disabled = !variant.available;
          submitButton.textContent = variant.available
            ? availableLabel
            : "Produto esgotado";
        }
        if (status) {
          status.textContent = variant.available
            ? ""
            : "Esta variante está esgotada.";
        }

        if (shouldSyncCarousel) {
          selectCarouselMedia?.(
            variant.featured_image?.id ||
              variant.featured_media?.id,
          );
        }
      };

      const openSizeDialog = () => {
        if (!sizeDialog || sizeDialog.open) return;
        sizeOpenButton?.setAttribute("aria-expanded", "true");
        if (typeof sizeDialog.showModal === "function") {
          try {
            sizeDialog.showModal();
            return;
          } catch (_) {}
        }
        sizeDialog.setAttribute("open", "");
      };

      const closeSizeDialog = () => {
        if (!sizeDialog) return;
        sizeOpenButton?.setAttribute("aria-expanded", "false");
        if (
          typeof sizeDialog.close === "function" &&
          sizeDialog.open
        ) {
          sizeDialog.close();
        } else {
          sizeDialog.removeAttribute("open");
        }
      };

      const handleOptionChange = (event) => {
        const changedColor = Boolean(
          event.currentTarget.closest(
            ".aqj-option-group--cor",
          ),
        );
        syncVariant(changedColor);
        if (changedColor) {
          colorDrawer?.removeAttribute("open");
        }
      };

      const syncColorDrawerState = () => {
        root.classList.toggle(
          "aqj-color-drawer-open",
          Boolean(colorDrawer?.open),
        );
      };

      const closeSizeDialogFromBackdrop = (event) => {
        if (event.target === sizeDialog) {
          closeSizeDialog();
        }
      };

      optionInputs.forEach((input) =>
        input.addEventListener("change", handleOptionChange),
      );
      colorDrawer?.addEventListener(
        "toggle",
        syncColorDrawerState,
      );
      sizeOpenButton?.addEventListener("click", openSizeDialog);
      sizeCloseButtons.forEach((button) =>
        button.addEventListener("click", closeSizeDialog),
      );
      sizeDialog?.addEventListener(
        "click",
        closeSizeDialogFromBackdrop,
      );
      syncColorDrawerState();
      syncVariant(false);

      cleanup.push(() => {
        root.classList.remove("aqj-color-drawer-open");
        optionInputs.forEach((input) =>
          input.removeEventListener(
            "change",
            handleOptionChange,
          ),
        );
        colorDrawer?.removeEventListener(
          "toggle",
          syncColorDrawerState,
        );
        sizeOpenButton?.removeEventListener(
          "click",
          openSizeDialog,
        );
        sizeCloseButtons.forEach((button) =>
          button.removeEventListener(
            "click",
            closeSizeDialog,
          ),
        );
        sizeDialog?.removeEventListener(
          "click",
          closeSizeDialogFromBackdrop,
        );
        closeSizeDialog();
      });
    }

    root._aqjCleanup = () => {
      isDisposed = true;
      cleanup.forEach((callback) => callback());
      delete root._aqjCleanup;
      delete root.dataset.aqjReady;
      window.setTimeout(() => {
        if (!document.querySelector(ROOT_SELECTOR)) {
          document.body.classList.remove("aqj-landing-active");
        }
      }, 0);
    };
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) {
      initRoot(scope);
    }
    scope
      .querySelectorAll?.(ROOT_SELECTOR)
      .forEach((root) => initRoot(root));
  }

  if (!window.AquaFitLisboaLanding) {
    window.AquaFitLisboaLanding = { initAll };
    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        () => initAll(),
        { once: true },
      );
    } else {
      initAll();
    }

    document.addEventListener(
      "shopify:section:load",
      (event) => initAll(event.target),
    );
    document.addEventListener(
      "shopify:section:unload",
      (event) => {
        const root = event.target.matches?.(ROOT_SELECTOR)
          ? event.target
          : event.target.querySelector?.(ROOT_SELECTOR);
        root?._aqjCleanup?.();
      },
    );
  } else {
    window.AquaFitLisboaLanding.initAll();
  }
})();
